import React, { useState, useEffect } from "react";
import "./PlansPage.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useNavigate } from "react-router-dom";
import {
    Wifi,
    Tv,
    ArrowLeft,
    CheckCircle,
    X,
    Plus
} from "lucide-react";
import {
    getPlanOttConfig,
    getOttsForPlanSpeed
} from "../utils/ottData";

const API_BASE =
    process.env.REACT_APP_API_BASE_URL ||
    "https://ritubroadband.in/api";

function PlansPage() {
    const navigate = useNavigate();

    const [broadbandPlans, setBroadbandPlans] = useState([]);
    const [ottAddonPlans, setOttAddonPlans] = useState([]);
    const [ottPlatforms, setOttPlatforms] = useState([]);

    const [selectedDuration, setSelectedDuration] = useState(1);
    const [selectedOttDuration, setSelectedOttDuration] = useState(3);

    const [ottConfig, setOttConfig] = useState(getPlanOttConfig());
    const [activeModalPlan, setActiveModalPlan] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    /* Load all plans once */
    useEffect(() => {
        let mounted = true;

        const loadPlans = async () => {
            try {
                setLoading(true);
                setError("");

                const [
                    broadbandResponse,
                    ottResponse,
                    addonResponse
                ] = await Promise.all([
                    fetch(`${API_BASE}/get_broadband_plans.php`),
                    fetch(`${API_BASE}/get_ott_platforms.php`),
                    fetch(`${API_BASE}/get_ott_addon_plans.php`)
                ]);

                if (!broadbandResponse.ok) {
                    throw new Error("Unable to load broadband plans");
                }

                if (!ottResponse.ok) {
                    throw new Error("Unable to load OTT platforms");
                }

                if (!addonResponse.ok) {
                    throw new Error("Unable to load OTT add-on plans");
                }

                const [
                    broadbandResult,
                    ottResult,
                    addonResult
                ] = await Promise.all([
                    broadbandResponse.json(),
                    ottResponse.json(),
                    addonResponse.json()
                ]);

                if (!mounted) {
                    return;
                }

                if (broadbandResult.status !== "success") {
                    throw new Error(
                        broadbandResult.message ||
                        "Unable to load broadband plans"
                    );
                }

                if (ottResult.status !== "success") {
                    throw new Error(
                        ottResult.message ||
                        "Unable to load OTT platforms"
                    );
                }

                if (addonResult.status !== "success") {
                    throw new Error(
                        addonResult.message ||
                        "Unable to load OTT add-on plans"
                    );
                }

                const broadbandData = Array.isArray(broadbandResult.data)
                    ? broadbandResult.data
                    : [];

                const ottData = Array.isArray(ottResult.data)
                    ? ottResult.data
                    : [];

                const addonData = Array.isArray(addonResult.data)
                    ? addonResult.data
                    : [];

                const normalizedBroadbandPlans =
                    broadbandData.map((plan) => ({
                        id: Number(plan.id),
                        speed: String(
                            plan.speed ?? ""
                        ),
                        label:
                            plan.label ||
                            plan.plan_name ||
                            `${plan.speed} Mbps`,
                        plan_name:
                            plan.plan_name ||
                            plan.label ||
                            `${plan.speed} Mbps`,
                        tagline: plan.tagline || "",
                        popular:
                            Number(plan.is_popular) === 1 ||
                            plan.is_popular === true,
                        is_popular:
                            Number(plan.is_popular) === 1 ||
                            plan.is_popular === true,
                        active:
                            Number(plan.active ?? 1) === 1,
                        display_order:
                            Number(plan.display_order ?? 0),
                        features: Array.isArray(plan.features)
                            ? plan.features
                            : [],
                        durations: Array.isArray(plan.durations)
                            ? plan.durations.map((duration) => ({
                                id: Number(duration.id || 0),
                                months: Number(
                                    duration.months ??
                                    duration.duration_months ??
                                    0
                                ),
                                price: Number(
                                    duration.price ?? 0
                                ),
                                label:
                                    duration.duration_label ||
                                    duration.label ||
                                    `${duration.months} Months`,
                                duration_label:
                                    duration.duration_label ||
                                    duration.label ||
                                    `${duration.months} Months`,
                                badge:
                                    duration.badge || "",
                                active:
                                    Number(
                                        duration.active ?? 1
                                    ) === 1
                            }))
                            : []
                    }));

                const normalizedOttPlatforms =
                    ottData.map((ott) => ({
                        id: Number(ott.id),
                        name:
                            ott.name ||
                            ott.ott_name ||
                            "",
                        ott_name:
                            ott.ott_name ||
                            ott.name ||
                            "",
                        logo: ott.logo || "",
                        display_order:
                            Number(ott.display_order ?? 0),
                        active:
                            Number(ott.active ?? 1) === 1
                    }));

                const normalizedAddonPlans =
                    addonData.map((plan) => ({
                        id: Number(plan.id),
                        months: Number(
                            plan.months ??
                            plan.duration_months ??
                            0
                        ),
                        duration_months: Number(
                            plan.duration_months ??
                            plan.months ??
                            0
                        ),
                        label:
                            plan.label ||
                            plan.duration_label ||
                            `${plan.months} Months`,
                        duration_label:
                            plan.duration_label ||
                            plan.label ||
                            `${plan.months} Months`,
                        price: Number(plan.price ?? 0),
                        is_popular:
                            Number(plan.is_popular) === 1 ||
                            plan.is_popular === true,
                        popular:
                            Number(plan.is_popular) === 1 ||
                            plan.is_popular === true,
                        badge: plan.badge || "",
                        description:
                            plan.description || "",
                        display_order:
                            Number(plan.display_order ?? 0),
                        active:
                            Number(plan.active ?? 1) === 1,
                        features: Array.isArray(plan.features)
                            ? plan.features
                            : []
                    }));

                if (normalizedBroadbandPlans.length > 0) {
                    const availableDurations = [];

                    normalizedBroadbandPlans.forEach((plan) => {
                        plan.durations.forEach((duration) => {
                            if (
                                duration.active &&
                                duration.months > 0 &&
                                !availableDurations.includes(
                                    duration.months
                                )
                            ) {
                                availableDurations.push(
                                    duration.months
                                );
                            }
                        });
                    });

                    availableDurations.sort(
                        (a, b) => a - b
                    );

                    if (
                        availableDurations.length > 0 &&
                        !availableDurations.includes(
                            selectedDuration
                        )
                    ) {
                        setSelectedDuration(
                            availableDurations[0]
                        );
                    }
                }

                if (normalizedAddonPlans.length > 0) {
                    const availableAddonDurations =
                        normalizedAddonPlans
                            .filter(
                                (plan) => plan.active
                            )
                            .map(
                                (plan) => plan.months
                            )
                            .filter(
                                (months) => months > 0
                            )
                            .sort(
                                (a, b) => a - b
                            );

                    if (
                        availableAddonDurations.length > 0 &&
                        !availableAddonDurations.includes(
                            selectedOttDuration
                        )
                    ) {
                        setSelectedOttDuration(
                            availableAddonDurations[0]
                        );
                    }
                }

                setBroadbandPlans(
                    normalizedBroadbandPlans.filter(
                        (plan) => plan.active
                    )
                );

                setOttPlatforms(
                    normalizedOttPlatforms.filter(
                        (ott) => ott.active
                    )
                );

                setOttAddonPlans(
                    normalizedAddonPlans.filter(
                        (plan) => plan.active
                    )
                );

            } catch (err) {
                console.error(
                    "Plans loading error:",
                    err
                );

                if (mounted) {
                    setError(
                        err.message ||
                        "Unable to load plans"
                    );
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadPlans();

        return () => {
            mounted = false;
        };
    }, []);

    /* Listen for OTT configuration changes */
    useEffect(() => {
        const handleConfigUpdate = (event) => {
            setOttConfig(
                event.detail ||
                getPlanOttConfig()
            );
        };

        window.addEventListener(
            "ottConfigUpdated",
            handleConfigUpdate
        );

        window.addEventListener(
            "storage",
            handleConfigUpdate
        );

        return () => {
            window.removeEventListener(
                "ottConfigUpdated",
                handleConfigUpdate
            );

            window.removeEventListener(
                "storage",
                handleConfigUpdate
            );
        };
    }, []);

    const scrollToForm = () => {
        navigate("/");

        setTimeout(() => {
            const form =
                document.getElementById(
                    "contactFormSection"
                );

            if (form) {
                form.scrollIntoView({
                    behavior: "smooth"
                });

                form.classList.add(
                    "highlight-form"
                );

                setTimeout(() => {
                    form.classList.remove(
                        "highlight-form"
                    );
                }, 2000);
            }
        }, 400);
    };

    const getPlanFeatures = (plan) => {
        if (
            Array.isArray(plan.features) &&
            plan.features.length > 0
        ) {
            return plan.features
                .filter((feature) => {
                    if (typeof feature === "string") {
                        return feature.trim() !== "";
                    }

                    return (
                        feature &&
                        String(
                            feature.feature_text || ""
                        ).trim() !== ""
                    );
                })
                .map((feature) => {
                    if (typeof feature === "string") {
                        return feature;
                    }

                    return feature.feature_text;
                });
        }

        return [
            "Committed speed on Wi-Fi Router",
            "100% Fiber to Home",
            "Same Upload & Download Speed",
            "Free Wi-Fi ONT (conditions apply)"
        ];
    };

    const getDurationLabel = (duration) => {
        if (
            duration.duration_label &&
            duration.duration_label.trim()
        ) {
            return duration.duration_label;
        }

        if (
            duration.label &&
            duration.label.trim()
        ) {
            return duration.label;
        }

        if (Number(duration.months) === 1) {
            return "1 Month";
        }

        return `${duration.months} Months`;
    };

    const getSelectedDuration = (plan) => {
        if (!plan || !Array.isArray(plan.durations)) {
            return null;
        }

        return plan.durations.find(
            (duration) =>
                Number(duration.months) ===
                Number(selectedDuration) &&
                duration.active !== false
        );
    };

    const getMonthlyDuration = (plan) => {
        if (!plan || !Array.isArray(plan.durations)) {
            return null;
        }

        return (
            plan.durations.find(
                (duration) =>
                    Number(duration.months) === 1 &&
                    duration.active !== false
            ) ||
            plan.durations
                .filter(
                    (duration) =>
                        duration.active !== false
                )
                .sort(
                    (a, b) =>
                        Number(a.months) -
                        Number(b.months)
                )[0]
        );
    };

    const modalOtts = activeModalPlan
        ? getOttsForPlanSpeed(
            activeModalPlan.speed,
            selectedDuration,
            ottConfig
        )
        : [];

    const availableBroadbandDurations =
        Array.from(
            new Set(
                broadbandPlans.flatMap((plan) =>
                    plan.durations
                        .filter(
                            (duration) =>
                                duration.active !== false
                        )
                        .map(
                            (duration) =>
                                Number(
                                    duration.months
                                )
                        )
                )
            )
        ).sort((a, b) => a - b);

    const availableOttDurations =
        Array.from(
            new Set(
                ottAddonPlans
                    .filter(
                        (plan) =>
                            plan.active !== false
                    )
                    .map(
                        (plan) =>
                            Number(plan.months)
                    )
            )
        ).sort((a, b) => a - b);

    return (
        <>
            <Header />

            <main className="plans-page">

                {/* Hero */}
                <section className="plans-hero">

                    <h1 className="plans-hero-title">
                        Broadband &amp; OTT Plans
                    </h1>

                    <p className="plans-hero-sub">
                        Pure fiber internet with lightning speed —
                        plus optional OTT &amp; Live TV add-ons.
                    </p>

                </section>

                {/* Loading */}
                {loading && (
                    <div
                        style={{
                            textAlign: "center",
                            padding: "60px 20px",
                            color: "var(--text-muted)"
                        }}
                    >
                        Loading plans...
                    </div>
                )}

                {/* Error */}
                {!loading && error && (
                    <div
                        style={{
                            textAlign: "center",
                            padding: "60px 20px",
                            color: "#dc2626"
                        }}
                    >
                        {error}
                    </div>
                )}

                {!loading && !error && (
                    <>
                        {/* Broadband Plans */}
                        <section className="plans-section">

                            <div className="plans-section-inner">

                                <div className="plans-section-head">

                                    <div className="plans-section-icon">
                                        <Wifi size={20} />
                                    </div>

                                    <div>
                                        <h2 className="plans-section-title">
                                            Internet Plans
                                        </h2>

                                        <p className="plans-section-sub">
                                            Choose your speed &amp; save more
                                            on longer durations
                                        </p>
                                    </div>

                                </div>

                                {/* Dynamic duration tabs */}
                                {availableBroadbandDurations.length > 0 && (
                                    <div className="duration-toggle">

                                        {availableBroadbandDurations.map(
                                            (months) => {

                                                const isMonthly =
                                                    months === 1;

                                                return (
                                                    <button
                                                        key={months}
                                                        id={`bb-duration-${months}`}
                                                        className={`duration-btn ${selectedDuration === months
                                                            ? "active"
                                                            : ""
                                                            }`}
                                                        onClick={() =>
                                                            setSelectedDuration(
                                                                months
                                                            )
                                                        }
                                                    >
                                                        {isMonthly
                                                            ? "Monthly"
                                                            : `${months} Months`}

                                                        {!isMonthly &&
                                                            months >= 12 && (
                                                                <span className="save-tag">
                                                                    Best Value
                                                                </span>
                                                            )}

                                                        {!isMonthly &&
                                                            months > 1 &&
                                                            months < 12 && (
                                                                <span className="save-tag">
                                                                    Save More
                                                                </span>
                                                            )}
                                                    </button>
                                                );
                                            }
                                        )}

                                    </div>
                                )}

                                {/* Dynamic broadband cards */}
                                <div className="plans-grid-full">

                                    {broadbandPlans.map(
                                        (plan) => {

                                            const selected =
                                                getSelectedDuration(
                                                    plan
                                                );

                                            const monthly =
                                                getMonthlyDuration(
                                                    plan
                                                );

                                            if (!selected) {
                                                return null;
                                            }

                                            const saving =
                                                monthly &&
                                                    Number(
                                                        selected.months
                                                    ) > 1
                                                    ? (
                                                        Number(
                                                            monthly.price
                                                        ) *
                                                        Number(
                                                            selected.months
                                                        )
                                                    ) -
                                                    Number(
                                                        selected.price
                                                    )
                                                    : 0;

                                            const planOtts =
                                                getOttsForPlanSpeed(
                                                    plan.speed,
                                                    selectedDuration,
                                                    ottConfig
                                                );

                                            const displayedOtts =
                                                planOtts.slice(
                                                    0,
                                                    3
                                                );

                                            const extraCount =
                                                planOtts.length >
                                                    3
                                                    ? planOtts.length -
                                                    3
                                                    : 0;

                                            return (
                                                <div
                                                    key={plan.id}
                                                    className={`plan-card-full ${plan.popular
                                                        ? "popular"
                                                        : ""
                                                        }`}
                                                >

                                                    {plan.popular && (
                                                        <div className="popular-badge-full">
                                                            ⭐ MOST POPULAR
                                                        </div>
                                                    )}

                                                    <div className="plan-speed-full">
                                                        {plan.speed}
                                                        <span>
                                                            Mbps
                                                        </span>
                                                    </div>

                                                    <p className="plan-tagline">
                                                        {plan.tagline}
                                                    </p>

                                                    <div className="plan-price-full">
                                                        ₹
                                                        {Number(
                                                            selected.price
                                                        ).toLocaleString(
                                                            "en-IN"
                                                        )}

                                                        <span>
                                                            /
                                                            {
                                                                getDurationLabel(
                                                                    selected
                                                                )
                                                            }
                                                        </span>
                                                    </div>

                                                    {saving > 0 && (
                                                        <div className="saving-tag">
                                                            🎉 Save ₹
                                                            {saving.toLocaleString(
                                                                "en-IN"
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* OTT preview */}
                                                    <div
                                                        className="plan-otts-compact-box"
                                                        onClick={() =>
                                                            setActiveModalPlan(
                                                                plan
                                                            )
                                                        }
                                                        title="Click to view all included OTT platforms"
                                                    >

                                                        <div className="plan-otts-compact-top">

                                                            <span className="plan-otts-label">
                                                                <Tv size={13} />
                                                                Included OTTs (
                                                                {
                                                                    planOtts.length
                                                                }
                                                                )
                                                            </span>

                                                            {extraCount >
                                                                0 && (
                                                                    <span className="plan-otts-top-count-badge">
                                                                        +
                                                                        {
                                                                            extraCount
                                                                        }{" "}
                                                                        More
                                                                    </span>
                                                                )}

                                                        </div>

                                                        {planOtts.length >
                                                            0 ? (
                                                            <div className="plan-otts-compact-icons">

                                                                {displayedOtts.map(
                                                                    (
                                                                        ott
                                                                    ) => (
                                                                        <div
                                                                            key={
                                                                                ott.id
                                                                            }
                                                                            className="plan-ott-mini-chip"
                                                                        >
                                                                            <img
                                                                                src={
                                                                                    ott.logo
                                                                                }
                                                                                alt={
                                                                                    ott.name
                                                                                }
                                                                                className="logo-ott-img"
                                                                            />
                                                                        </div>
                                                                    )
                                                                )}

                                                                {extraCount > 0 && (
                                                                    <div className="plan-ott-more-icon-circle">
                                                                        +{extraCount}
                                                                    </div>
                                                                )}

                                                            </div>
                                                        ) : (
                                                            <div className="plan-no-otts">
                                                                No OTTs configured yet
                                                            </div>
                                                        )}

                                                    </div>

                                                    {/* Dynamic features */}
                                                    <ul className="plan-features">

                                                        {getPlanFeatures(
                                                            plan
                                                        ).map(
                                                            (
                                                                feature,
                                                                index
                                                            ) => (
                                                                <li
                                                                    key={
                                                                        index
                                                                    }
                                                                >
                                                                    <CheckCircle
                                                                        size={
                                                                            14
                                                                        }
                                                                    />
                                                                    {
                                                                        feature
                                                                    }
                                                                </li>
                                                            )
                                                        )}

                                                    </ul>

                                                    <button
                                                        className="plan-cta-btn"
                                                        onClick={
                                                            scrollToForm
                                                        }
                                                    >
                                                        Get Connected
                                                    </button>

                                                </div>
                                            );
                                        }
                                    )}

                                </div>

                            </div>

                        </section>

                        {/* OTT Add-on section */}
                        <section className="ott-addon-section">

                            <div className="ott-addon-inner">

                                <div className="plans-section-head light">

                                    <div className="plans-section-icon accent">
                                        <Tv size={20} />
                                    </div>

                                    <div>

                                        <h2 className="plans-section-title light">
                                            OTT + 300 Live Channels Add-on
                                        </h2>

                                        <p className="plans-section-sub light">
                                            Add OTT platforms &amp; 300+ FTA
                                            Live TV channels on top of your
                                            internet plan
                                        </p>

                                    </div>

                                </div>

                                {/* Dynamic OTT platforms */}
                                <div className="ott-logos-grid">

                                    {ottPlatforms.map(
                                        (ott) => (
                                            <div
                                                key={ott.id}
                                                className="ott-name-chip"
                                            >
                                                {ott.name}
                                            </div>
                                        )
                                    )}

                                </div>

                                {/* Dynamic OTT durations */}
                                {availableOttDurations.length >
                                    0 && (
                                        <div className="duration-toggle ott-duration-toggle">

                                            {availableOttDurations.map(
                                                (months) => {

                                                    const selectedPlan =
                                                        ottAddonPlans.find(
                                                            (
                                                                plan
                                                            ) =>
                                                                Number(
                                                                    plan.months
                                                                ) ===
                                                                months
                                                        );

                                                    return (
                                                        <button
                                                            key={
                                                                months
                                                            }
                                                            id={`ott-duration-${months}`}
                                                            className={`duration-btn ${selectedOttDuration ===
                                                                months
                                                                ? "active"
                                                                : ""
                                                                }`}
                                                            onClick={() =>
                                                                setSelectedOttDuration(
                                                                    months
                                                                )
                                                            }
                                                        >
                                                            {months}{" "}
                                                            Months

                                                            {selectedPlan?.is_popular && (
                                                                <span className="save-tag">
                                                                    Best Value
                                                                </span>
                                                            )}
                                                        </button>
                                                    );
                                                }
                                            )}

                                        </div>
                                    )}

                                {/* OTT addon cards */}
                                <div className="ott-addon-grid">

                                    {ottAddonPlans.map(
                                        (combo) => {

                                            const isActive =
                                                Number(
                                                    combo.months
                                                ) ===
                                                Number(
                                                    selectedOttDuration
                                                );

                                            const features =
                                                combo.features &&
                                                    combo.features.length >
                                                    0
                                                    ? combo.features
                                                        .filter(
                                                            (
                                                                feature
                                                            ) =>
                                                                typeof feature ===
                                                                    "string"
                                                                    ? feature.trim() !==
                                                                    ""
                                                                    : String(
                                                                        feature.feature_text ||
                                                                        ""
                                                                    ).trim() !==
                                                                    ""
                                                        )
                                                        .map(
                                                            (
                                                                feature
                                                            ) =>
                                                                typeof feature ===
                                                                    "string"
                                                                    ? feature
                                                                    : feature.feature_text
                                                        )
                                                    : [
                                                        `${ottPlatforms.length} OTT Platforms`,
                                                        "300+ FTA Live Channels",
                                                        "Netflix, JioHotstar & more",
                                                        "Active broadband required"
                                                    ];

                                            return (
                                                <div
                                                    key={
                                                        combo.id
                                                    }
                                                    className={`ott-addon-card ${combo.popular
                                                        ? "ott-popular"
                                                        : ""
                                                        } ${isActive
                                                            ? "ott-active"
                                                            : ""
                                                        }`}
                                                >

                                                    {combo.popular && (
                                                        <div className="ott-badge">
                                                            {combo.badge ||
                                                                "Best Value"}
                                                        </div>
                                                    )}

                                                    <div className="ott-addon-duration">
                                                        {
                                                            combo.label
                                                        }
                                                    </div>

                                                    <div className="ott-addon-price">

                                                        ₹
                                                        {Number(
                                                            combo.price
                                                        ).toLocaleString(
                                                            "en-IN"
                                                        )}

                                                        <span className="ott-addon-period">
                                                            /
                                                            {Number(
                                                                combo.months
                                                            ) ===
                                                                1
                                                                ? "month"
                                                                : `${combo.months} months`}
                                                        </span>

                                                    </div>

                                                    <p className="ott-addon-per-month">
                                                        ≈ ₹
                                                        {Math.round(
                                                            Number(
                                                                combo.price
                                                            ) /
                                                            Number(
                                                                combo.months
                                                            )
                                                        )}
                                                        /month
                                                    </p>

                                                    <ul className="ott-addon-features">

                                                        {features.map(
                                                            (
                                                                feature,
                                                                index
                                                            ) => (
                                                                <li
                                                                    key={
                                                                        index
                                                                    }
                                                                >
                                                                    <CheckCircle
                                                                        size={
                                                                            13
                                                                        }
                                                                    />
                                                                    {
                                                                        feature
                                                                    }
                                                                </li>
                                                            )
                                                        )}

                                                    </ul>

                                                    <button
                                                        className="plan-cta-btn ott-cta"
                                                        onClick={
                                                            scrollToForm
                                                        }
                                                    >
                                                        Get This Add-on
                                                    </button>

                                                </div>
                                            );
                                        }
                                    )}

                                </div>

                            </div>

                        </section>

                        {/* Footer note */}
                        <div className="plans-footer-note">

                            <p>
                                * All prices are exclusive of GST.
                                OTT add-on plans require an active
                                broadband subscription.
                            </p>

                            <p>
                                For queries call:{" "}
                                <a href="tel:7702935830">
                                    7702935830
                                </a>{" "}
                                /{" "}
                                <a href="tel:8500168970">
                                    8500168970
                                </a>
                            </p>

                        </div>
                    </>
                )}

            </main>

            {/* OTT Detail Popup */}
            {activeModalPlan && (
                <div
                    className="ott-modal-overlay"
                    onClick={() =>
                        setActiveModalPlan(null)
                    }
                >

                    <div
                        className="ott-modal-card"
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                    >

                        <button
                            className="ott-modal-close"
                            onClick={() =>
                                setActiveModalPlan(
                                    null
                                )
                            }
                        >
                            <X size={20} />
                        </button>

                        <div className="ott-modal-header">

                            <div className="ott-modal-badge">
                                <Tv size={20} />
                            </div>

                            <div>

                                <h3 className="ott-modal-title">
                                    {
                                        activeModalPlan.label
                                    }{" "}
                                    Broadband Plan
                                </h3>

                                <p className="ott-modal-sub">
                                    Included OTT Apps &amp;
                                    Subscriptions (
                                    {modalOtts.length})
                                </p>

                            </div>

                        </div>

                        <div className="ott-modal-body">

                            {modalOtts.length > 0 ? (
                                <div className="ott-modal-grid">

                                    {modalOtts.map(
                                        (ott) => (
                                            <div
                                                key={
                                                    ott.id
                                                }
                                                className="ott-modal-item"
                                            >

                                                <img
                                                    src={
                                                        ott.logo
                                                    }
                                                    alt={
                                                        ott.name
                                                    }
                                                    className="ott-modal-img"
                                                />

                                                <div className="ott-modal-info">

                                                    <span className="ott-modal-name">
                                                        {
                                                            ott.name
                                                        }
                                                    </span>

                                                    <span className="ott-modal-tag">
                                                        <CheckCircle
                                                            size={
                                                                12
                                                            }
                                                        />
                                                        Included
                                                    </span>

                                                </div>

                                            </div>
                                        )
                                    )}

                                </div>
                            ) : (
                                <p className="ott-modal-empty">
                                    No OTT apps currently
                                    assigned to this plan.
                                </p>
                            )}

                        </div>

                        <div className="ott-modal-footer">

                            <button
                                className="plan-cta-btn"
                                onClick={() => {
                                    setActiveModalPlan(
                                        null
                                    );
                                    scrollToForm();
                                }}
                            >
                                Subscribe to{" "}
                                {
                                    activeModalPlan.label
                                }{" "}
                                Plan
                            </button>

                        </div>

                    </div>

                </div>
            )}

            <Footer />
        </>
    );
}

export default PlansPage;